/**
 * Which cited items a search is about (owner, 2026-10-14: "searching the violation words shows the printouts for it"). Pure: a query in,
 * FDA Form 3-A item numbers out, from sources already in the repo and nothing invented:
 *
 *   "item 22" / "item #38"      the number itself (1 to 56)
 *   "flies"                     a cited plan's own words (citedplan.ts CITED_PLANS: the flies plan is item 38)
 *   an inspector's phrase       the violation-article matcher (violationnotes.ts matchNote: "no soap at the hand sink" → item 10)
 *   "cold holding", "hand sink" the form's own item title (inspectionform.ts FORM_ITEMS) and the plain name the app uses for it
 *                               (lastinspection.ts INSPECTION_COVER topic), every word of the query a whole word or a word start
 *
 * A query that names more than MAX_ITEMS items is too general to be about one citation ("temperature"): no items, so the page shows
 * only its ordinary results. Never guesses: no match is an empty list.
 */
import { norm, wordScore } from "./findmatch.js";
import { CITED_PLANS } from "./citedplan.ts";
import { FORM_ITEMS } from "./inspectionform.ts";
import { INSPECTION_COVER } from "./lastinspection.ts";
import { matchNote } from "./violationnotes.ts";

export const MAX_CITATION_ITEMS = 3;
export const CITATION_LABEL = "For this citation";
const STOP = new Set(["the", "a", "an", "and", "of", "for", "to", "in", "on", "is", "my", "no", "not", "our", "we", "have", "with", "how", "do", "i"]);

let HAY: { n: number; title: string[]; topic: string; words: string[] }[] | undefined;
const hay = () => (HAY ??= FORM_ITEMS.map((f) => {
  const title = norm(f.title).split(" ").filter(Boolean), topic = norm(INSPECTION_COVER[f.n]?.topic ?? "").replace(/^(the|a|an) /, "");
  return { n: f.n, title, topic, words: [...title, ...topic.split(" ")].filter(Boolean) };
}));

export function citationItemsFor(query: string): number[] {
  const q = query.trim();
  if (q.length < 3) return [];
  const byNumber = /\bitems?\s*#?\s*(\d{1,2})\b/i.exec(q);
  if (byNumber) { const n = Number(byNumber[1]); return n >= 1 && n <= 56 ? [n] : []; }
  const found = new Set<number>();
  for (const p of CITED_PLANS) if (p.words.test(q) && !p.notWords?.test(q)) for (const n of p.items) found.add(n);
  const note = matchNote({ item: null, note: q });
  if (note.strength === "strong" && note.item != null) found.add(note.item);
  const tokens = norm(q).split(" ").filter((w) => w.length >= 3 && !STOP.has(w));
  // One word alone must be a whole word of the item's title, or the plain name the app uses for it ("pests"), never a piece of one
  // ("machines" is only part of "the dish sink or machine"); two or more words must each be a word, or a word start, of the title or name.
  if (tokens.length === 1) {
    const t = tokens[0]!;
    if (q.replace(/[^a-z]/gi, "").length >= 5) for (const h of hay()) if (h.topic === t || h.title.includes(t)) found.add(h.n);
  } else if (tokens.length > 1) {
    for (const h of hay()) if (tokens.every((t) => h.words.some((w) => wordScore(t, w) >= 0.9))) found.add(h.n);
  }
  return found.size > MAX_CITATION_ITEMS ? [] : [...found].sort((a, b) => a - b);
}
